import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import * as FileSaver from 'file-saver';

import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMadeIcon from '@mui/icons-material/CallMade';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { GET_LIST, provider, history } from '../_utils';
import { DateRangeInput, StatusChip, LoadingStub } from '../_Components';
import ShowCall from './ShowCall';
import {
    callDir,
    durationOf,
    formatPhone,
    formatTime,
    mmss,
    operatorLabel,
    recordAgent,
    recordAgentLogin,
    recordId,
    recordPhone,
    recordQueue,
    recordWhen,
    readSet,
    writeSet,
    FLAG_KEY,
} from './utils';
import './visor.css';

const VisorList = () => {
    const { id } = useParams();
    const [operators, setOperators] = useState([]);
    const [queues, setQueues] = useState([]);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [interval, setInterval] = useState({
        startDate: moment().startOf('day'),
        endDate: moment().endOf('day'),
    });
    const [phone, setPhone] = useState('');
    const [phoneDebounced, setPhoneDebounced] = useState('');
    const [agent, setAgent] = useState('');
    const [queue, setQueue] = useState('');
    const [flags, setFlags] = useState(() => readSet(FLAG_KEY));
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(50);
    const listRef = useRef(null);

    useEffect(() => {
        const t = setTimeout(() => setPhoneDebounced(phone.replace(/\D/g, '')), 350);
        return () => clearTimeout(t);
    }, [phone]);

    const intervalKey = [
        interval && interval.startDate && moment(interval.startDate).valueOf(),
        interval && interval.endDate && moment(interval.endDate).valueOf(),
    ].join(':');

    const load = useCallback(async () => {
        setLoading(true);
        const filter = { interval };
        if (phoneDebounced) filter.phone = phoneDebounced;
        if (queue) filter.queue = queue;
        if (agent) filter.agent = agent;
        try {
            const { status, data, total: count, operators: nextOps, queues: nextQueues } = await provider(
                GET_LIST,
                '/visor',
                {
                    filter,
                    sort: { field: 'StartTime', order: 'ASC' },
                    pagination: { page, perPage },
                }
            );
            if (status === 200) {
                setRows(data || []);
                setTotal(typeof count === 'number' ? count : (data || []).length);
                if (Array.isArray(nextOps)) setOperators(nextOps);
                if (Array.isArray(nextQueues)) setQueues(nextQueues);
            }
        } catch (e) { /* aborted */ }
        setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intervalKey, phoneDebounced, queue, agent, page, perPage]);

    useEffect(() => {
        setPage(1);
    }, [intervalKey, phoneDebounced, queue, agent, perPage]);

    useEffect(() => {
        load();
    }, [load]);

    const visible = rows;

    const selected = useMemo(() => {
        if (!visible.length) return null;
        if (id) {
            const found = visible.find((row) => String(recordId(row)) === String(id));
            if (found) return found;
        }
        return visible[0];
    }, [visible, id]);

    const selectedIndex = selected
        ? visible.findIndex((row) => recordId(row) === recordId(selected))
        : -1;

    const pageCount = Math.max(1, Math.ceil((total || 0) / perPage));
    const from = total ? (page - 1) * perPage + 1 : 0;
    const to = Math.min(page * perPage, total || visible.length);
    const countLabel = total
        ? `${from}–${to} из ${total}`
        : String(visible.length);

    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = 0;
    }, [page]);

    const openRow = (row) => {
        const rid = recordId(row);
        if (!rid) return;
        if (String(id) === String(rid)) return;
        const params = [];
        if (row.uid) params.push(`uid=${encodeURIComponent(row.uid)}`);
        if (row.sensyId) params.push(`sensyId=${encodeURIComponent(row.sensyId)}`);
        history.push(`/visor/${rid}${params.length ? `?${params.join('&')}` : ''}`);
    };

    const goRel = (delta) => {
        if (selectedIndex < 0) return;
        if (delta > 0 && selectedIndex === visible.length - 1 && page < pageCount) {
            setPage(page + 1);
            return;
        }
        if (delta < 0 && selectedIndex === 0 && page > 1) {
            setPage(page - 1);
            return;
        }
        const next = visible[selectedIndex + delta];
        if (next) openRow(next);
    };

    const toggleFlag = (row) => {
        const rid = String(recordId(row));
        if (!rid) return;
        const next = new Set(flags);
        if (next.has(rid)) next.delete(rid);
        else next.add(rid);
        setFlags(next);
        writeSet(FLAG_KEY, next);
    };

    const exportList = () => {
        const header = ['Время', 'Телефон', 'Оператор', 'Очередь', 'Длительность', 'Тип'];
        const body = visible.map((row) => [
            formatTime(recordWhen(row)),
            formatPhone(recordPhone(row)),
            recordAgent(row) || '',
            recordQueue(row) || '',
            mmss(durationOf(row)),
            callDir(row) === 'in' ? 'входящий' : 'исходящий',
        ].join(';'));
        const csv = `\uFEFF${[header.join(';'), ...body].join('\n')}`;
        FileSaver.saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'visor.csv');
    };

    const opName = (login) => {
        const found = operators.find((op) => op.login === login || op.name === login);
        return found ? operatorLabel(found) : login;
    };

    const agentOptions = useMemo(() => {
        const fromOps = operators.map((op) => ({
            id: op.login,
            name: `${operatorLabel(op)}${op.login ? ` · ${op.login}` : ''}`,
        })).filter((op) => op.id);
        const extra = [...new Set(rows.map((row) => recordAgentLogin(row)).filter(Boolean))]
            .filter((login) => !fromOps.some((op) => op.id === login))
            .map((login) => ({ id: login, name: login }));
        return [...fromOps, ...extra];
    }, [operators, rows]);

    return (
        <div className="pc-page pc-ls">
            <div className="pc-ls-top">
                <h1>Прослушка</h1>
                <div className="pc-ls-dates">
                    <DateRangeInput
                        input={{ value: interval, onChange: setInterval }}
                        maxDate={new Date()}
                    />
                </div>
                <div className="pc-ls-srch">
                    <SearchIcon />
                    <input
                        className="pc-ls-inp"
                        placeholder="Телефон"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                    />
                </div>
                <select className="pc-ls-inp" value={agent} onChange={(e) => setAgent(e.target.value)}>
                    <option value="">Все операторы</option>
                    {agentOptions.map((op) => (
                        <option key={op.id} value={op.id}>{op.name}</option>
                    ))}
                </select>
                <select className="pc-ls-inp" value={queue} onChange={(e) => setQueue(e.target.value)}>
                    <option value="">Все очереди</option>
                    {(queues || []).map((item) => (
                        <option key={item._id || item.id || item.name} value={item.id || item._id || item.name}>
                            {item.name || item.id}
                        </option>
                    ))}
                </select>
                <button type="button" className="pc-ls-btn" onClick={exportList}>
                    <DownloadIcon sx={{ fontSize: 14 }} />
                    Выгрузить список
                </button>
            </div>

            <div className="pc-ls-stage">
                <div className="pc-ls-panel">
                    <div className="pc-ls-phd">
                        <h2>Записи</h2>
                        <StatusChip tone="mono">{countLabel}</StatusChip>
                    </div>
                    <div className="pc-ls-pbd" ref={listRef}>
                        {loading && !rows.length ? (
                            <>
                                <div className="pc-ls-skel" />
                                <div className="pc-ls-skel" />
                                <div className="pc-ls-skel" />
                            </>
                        ) : visible.length ? visible.map((row) => {
                            const rid = recordId(row);
                            const cur = selected && recordId(selected) === rid;
                            const dir = callDir(row);
                            return (
                                <button
                                    key={rid}
                                    type="button"
                                    className={`pc-ls-rec${cur ? ' is-cur' : ''}`}
                                    onClick={() => openRow(row)}
                                >
                                    <span className={`pc-ls-arr ${dir}`}>
                                        {dir === 'in'
                                            ? <CallReceivedIcon sx={{ fontSize: 11 }} />
                                            : <CallMadeIcon sx={{ fontSize: 11 }} />
                                        }
                                    </span>
                                    <span className="pc-ls-rec__mid">
                                        <span className="pc-ls-rec__ph">{formatPhone(recordPhone(row))}</span>
                                        <span className="pc-ls-rec__meta">
                                            {recordAgent(row) || opName(recordAgentLogin(row))}{recordQueue(row) ? ` · ${recordQueue(row)}` : ''}
                                        </span>
                                    </span>
                                    <span className="pc-ls-rec__rt">
                                        <span className="pc-ls-rec__tm">{formatTime(recordWhen(row))}</span>
                                        <span className="pc-ls-rec__dur">{mmss(durationOf(row))}</span>
                                    </span>
                                </button>
                            );
                        }) : (
                            <div className="pc-ls-empty">
                                <b>Ничего не найдено</b>
                                Измените фильтры или интервал.
                            </div>
                        )}
                    </div>
                    {total > 0 && (
                        <div className="pc-ls-pager">
                            <select
                                className="pc-ls-inp pc-ls-pager__size"
                                value={perPage}
                                onChange={(e) => setPerPage(Number(e.target.value))}
                                title="Записей на странице"
                            >
                                {[25, 50, 100, 200].map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                            <span className="pc-ls-pager__n">{page} / {pageCount}</span>
                            <div className="pc-ls-navbtns">
                                <button
                                    type="button"
                                    title="Назад"
                                    disabled={page <= 1 || loading}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                >
                                    <ChevronLeftIcon sx={{ fontSize: 16 }} />
                                </button>
                                <button
                                    type="button"
                                    title="Вперёд"
                                    disabled={page >= pageCount || loading}
                                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                                >
                                    <ChevronRightIcon sx={{ fontSize: 16 }} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {selected
                    ? (
                        <ShowCall
                            record={selected}
                            onPrev={() => goRel(-1)}
                            onNext={() => goRel(1)}
                            flagged={flags.has(String(recordId(selected)))}
                            onFlag={() => toggleFlag(selected)}
                        />
                    )
                    : (
                        <div className="pc-ls-panel">
                            {loading
                                ? <LoadingStub title="Загружаем записи" />
                                : (
                                    <div className="pc-ls-empty">
                                        <b>Запись не выбрана</b>
                                        Выберите звонок слева.
                                    </div>
                                )
                            }
                        </div>
                    )
                }
            </div>
        </div>
    );
};

export default VisorList;
